import { colorSchemeDark, themeQuartz } from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'

/**
 * The house AG Grid theme: Quartz + the dark colour scheme, repainted with our
 * own palette so a grid reads as part of the app rather than a widget dropped
 * into it.
 *
 * Two rules this file exists to enforce in ONE place:
 *
 * 1. `accentColor` is `colors.PRIMARY` — tenant-aware. `PRIMARY_300` is always
 *    the SportNinja yellow and would paint Canlan's sort arrows and focus rings
 *    the wrong brand colour.
 * 2. `fontFamily: 'inherit'` — the wrapper element carries `${font.body}`
 *    (Inter on SportNinja, Gilroy on Canlan) and the grid inherits it. The
 *    tenant font lives in an Emotion `SerializedStyles` block, which is an
 *    object, NOT a font-family string — passing it here would serialize to
 *    garbage.
 *
 * Contrast follows the app's antd tables (`Statistics`): a `SECONDARY_300`
 * header over rows that alternate `SECONDARY_300` / `SECONDARY_400`, separated
 * by hairlines rather than by thick page-coloured gutters. Banding is what
 * carries the eye across a 10-column sheet; the gutters this file used to draw
 * were a second, competing banding system.
 */
export const dataGridTheme = themeQuartz.withPart(colorSchemeDark).withParams({
  accentColor: colors.PRIMARY,
  // The row surface, one step ABOVE the page.
  backgroundColor: colors.SECONDARY_300,
  foregroundColor: colors.NEUTRAL_0,
  cellTextColor: colors.NEUTRAL_0,
  borderColor: colors.SECONDARY_100,
  browserColorScheme: 'dark',

  headerBackgroundColor: colors.SECONDARY_300,
  headerTextColor: colors.NEUTRAL_0,
  // Quartz rules a vertical line between every header cell. On a 10-column
  // money sheet that reads as cage bars; the banded rows carry the structure.
  headerColumnBorder: false,
  columnBorder: false,
  // Hairlines, not gutters: the zebra below is the banding, and a 3px band of
  // page colour between every row on top of it reads as a broken table.
  headerRowBorder: { style: 'solid', width: 1, color: colors.SECONDARY_200 },
  rowBorder: { style: 'solid', width: 1, color: colors.SECONDARY_200 },
  wrapperBorder: { style: 'solid', width: 1, color: colors.SECONDARY_100 },
  // No frozen-pane seam, for the same reason as `columnBorder` above: a vertical
  // rule reads as a cage bar on a money sheet. Quartz draws one either side of a
  // pinned section by default, and in the page colour it lands as a black slab
  // beside PLAYER and ACTIONS — visible even when nothing is scrolled, which is
  // most of the time. The pinned columns simply sit flush with the rest.
  pinnedColumnBorder: false,

  // Zebra, matching the antd tables: first row on the base surface, the next one
  // step down. AG Grid counts from zero, so `.ag-row-odd` is the SECOND row —
  // the same order Statistics renders (`nth-child(odd)` is its first).
  oddRowBackgroundColor: colors.SECONDARY_400,
  rowHoverColor: colors.SECONDARY_200,

  // The pinned totals band reads as a footer: one step brighter again.
  pinnedRowBackgroundColor: colors.SECONDARY_200,
  pinnedRowTextColor: colors.NEUTRAL_0,

  fontFamily: 'inherit',
  fontSize: 14,
  wrapperBorderRadius: 8,
  cellHorizontalPadding: 14,
})

export default dataGridTheme
